import React, {forwardRef, useEffect, useImperativeHandle, useRef, useCallback} from 'react';

interface CaptchaProps {
    length?: number;
    width?: number;
    height?: number;
    onChange?: (text: string) => void;
    code?: string;
}

export interface CaptchaHandle {
    refresh: () => void;
}

const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(({
                                                             length = 6,
                                                             width = 100,
                                                             height = 40,
                                                             onChange,
                                                             code,
                                                         }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const generateText = useCallback(() => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
        let text = '';
        for (let i = 0; i < length; i++) {
            text += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return text;
    }, [length]);

    const drawCaptcha = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const captchaText = code ?? generateText();
        if (!code && onChange) onChange(captchaText);

        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(0, 0, width, height);
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#333';
        ctx.textBaseline = 'middle';

        const charWidth = width / length;
        for (let i = 0; i < length; i++) {
            const char = captchaText.charAt(i);
            const x = i * charWidth + Math.random() * 5;
            const y = height / 2 + (Math.random() * 10 - 5);
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate((Math.random() - 0.5) * 0.4);
            ctx.fillText(char, 0, 0);
            ctx.restore();
        }

        for (let i = 0; i < 30; i++) {
            ctx.beginPath();
            ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI);
            ctx.fillStyle = '#999';
            ctx.fill();
        }

        for (let i = 0; i < 2; i++) {
            ctx.beginPath();
            ctx.moveTo(Math.random() * width, Math.random() * height);
            ctx.lineTo(Math.random() * width, Math.random() * height);
            ctx.strokeStyle = '#aaa';
            ctx.stroke();
        }
    }, [length, width, height, onChange, code, generateText]);

    useEffect(() => {
        drawCaptcha();
    }, [drawCaptcha]);

    // ✨ 暴露 refresh 方法給外部
    useImperativeHandle(ref, () => ({
        refresh: drawCaptcha,
    }));

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            onClick={drawCaptcha}
            style={{ cursor: 'pointer', borderRadius: 4, border: '1px solid #ccc' }}
            title="點擊刷新驗證碼"
        />
    );
});

Captcha.displayName = 'Captcha';

export default Captcha;
